/* eslint-disable */
import Typography from '@material-ui/core/Typography';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { compose } from 'redux';
import withServices from '../../Contexts/Services/WithServices.context';
import throwI18nInvalidKeyLengthError from '../../Errors/I18n/I18nInvalidKeyLength.error';
import throwI18nInvalidKeyTypeError from '../../Errors/I18n/I18nInvalidKeyType.error';
import throwI18nNotEmptyTypographPropError from '../../Errors/I18n/I18nNotEmptyTypographProp.error';
import isString from '../../Support/Is/String/IsString.support';
import I18nPropTypes from './I18n.component.props';

/**
 * I18nComponent
 * @export
 * @class I18nComponent
 * @extends {React.Component<I18nComponentComposedProps, I18nComponentState>}
 */
export class I18nComponent extends React.Component {
  /**
   * The `props` to seed when any specific prop receives an `undefined` value. **NOT** a `null` value
   * @static
   * @type {Partial<I18nComponentProps>}
   * @memberof I18nComponent
   */
  static defaultProps = {};

  /**
   * The name of the Component to display when debugging
   * @static
   * @type {`I18n`}
   * @memberof I18nComponent
   */
  static displayName = `I18n`;

  /**
   * The values to use to apply runtime typechecking on the props
   * @static
   * @type {object}
   * @memberof I18nComponent
   */
  static propTypes = I18nPropTypes;

  /**
   * Data specific to the `<I18nComponent />` instance
   * @type {I18nComponentState}
   * @memberof I18nComponent
   */
  state = {
    text: ``,
  };

  /**
   * A method to be called when unmounting to remove the listener to the `I18nService`
   * @type {VoidFunction}
   * @memberof I18nComponent
   */
  unsubscribe;

  /**
   * When the Component mounts, try and fetch the translation for the provided key
   * @returns {void} void
   * @memberof I18nComponent
   */
  componentDidMount() {
    this.performTranslation();
  }

  /**
   * If `this.props.children` ever changes, make sure that we relisten to the new `key`
   * @param {React.props} prevProps The `props` prior to the update
   * @returns {void} void
   * @memberof I18nComponent
   */
  componentDidUpdate(prevProps) {
    const { children, data } = this.props;
    if (children !== prevProps.children || !isEqual(data, prevProps.data)) {
      this.unsubscribe();
      this.performTranslation();
    }
  }

  /**
   * Remove the listener to the `I18nService`
   * @returns {void} void
   * @memberof I18nComponent
   */
  componentWillUnmount() {
    this.unsubscribe();
  }

  /**
   * Get the key to use in the `I18nService`
   * @returns {string} The `key` to use
   * @memberof I18nComponent
   */
  getKey = () => {
    const { children } = this.props;
    if (!isString(children)) {
      return throwI18nInvalidKeyTypeError(children);
    }
    /** @type {string} */
    const key = children.trim();
    if (key.length === 0) {
      return throwI18nInvalidKeyLengthError(key);
    }
    return key;
  };

  /**
   * Handle when a translation is observed
   * @param {Error | null} error The possible `error` that may of been encountered
   * @param {I18nServiceLocaleChangedHandlerPayload} payload The `payload` received from the translation
   * @returns {void} void
   * @memberof I18nComponent
   */
  handleOnTranslation = (error, payload) => {
    const { key, lang, translation } = payload;
    if (error) {
      throw error;
    }
    this.setState({
      text: translation || `${key}`,
    });
  };

  /**
   * Get, and validate the translation for the specified `key`
   * @returns {void} void
   * @memberof I18nComponent
   */
  performTranslation = () => {
    const key = this.getKey();
    const { data, services } = this.props;
    this.unsubscribe = services.i18nService.observeTranslation(key, data, this.handleOnTranslation);
  };

  /**
   * Ensure that the given prop does not exist. This is done so that we won't get conflicts with `<Typography />`
   * @param {string} prop The prop to check
   * @param {string} name The name of the `prop`
   * @returns {void} void
   * @memberof I18nComponent
   */
  validateEmptyTypographyProp = (prop, name) => {
    if (prop !== undefined) {
      const key = this.getKey();
      return throwI18nNotEmptyTypographPropError(key, name, prop);
    }
  };

  /**
   * Render the `text` without using `<Typography />`
   * @param {React.Props} props The props of the Componet to use for rendering
   * @returns {React.Component} The rendering `<Component />`
   * @memberof I18nComponent
   */
  renderText = (props) => {
    const { align, color, component, gutterBottom, nowrap, paragraph, ...componentProps } = props;
    const { text } = this.state;
    // Because no `variant` has been defined, we're imposing a hard limit on props that can now be used.
    this.validateEmptyTypographyProp(color, `color`);
    this.validateEmptyTypographyProp(gutterBottom, `gutterBottom`);
    this.validateEmptyTypographyProp(nowrap, `nowrap`);
    this.validateEmptyTypographyProp(paragraph, `paragraph`);
    // If there is no noticable styling config, just return the text itself. This can be usefule when consuming an `<I18n />` in another prop.
    // Eg:
    // ```jsx
    // <TextField
    //   label={<I18n>LoginPage.Username</I18n>}
    //   type="text"
    //   value="example@website.com"
    // />
    // ```
    if (props.className === undefined && component === undefined && props.style === undefined) {
      return text;
    }
    // We define a `defaultProp` here so that we don't define it earlier which would cause it to be consumed by `<Typography />`
    const Component = component || `span`;
    return <Component {...componentProps}>{text}</Component>;
  };

  /**
   * Merge single style props with an overall `style` prop
   * @param {Object} style The style prop to use a a basis for the merge
   * @param {string} styleProps The map of props to merge. Eg: { color: 'red', display: 'block' }
   * @returns {Object} The merged `style` prop
   * @memberof I18nComponent
   */
  mergeStyleProp = (style, styleProps) => {
    const newStyle = {
      ...Object.entries(styleProps).reduce((prev, [key, value]) => ({ ...prev, ...(value ? { [key]: value } : {}) }), {}),
      ...(style || {}),
    };
    if (Object.keys(newStyle).length === 0) {
      return {};
    }
    return {
      style: newStyle,
    };
  };

  /**
   * Render the component
   * @returns {React.Component} The rendered `<I18nComponent />`
   * @memberof I18nComponent
   */
  render() {
    const { text } = this.state;
    if (text.length === 0) {
      return null;
    }
    const { actions, align, children, classes, data, services, style, textTransform, theme, variant, ...rest } = this.props;
    const typographyProps = {
      ...rest,
      ...this.mergeStyleProp(style, { textTransform }),
      ...(align === undefined ? {} : { align }),
    };
    if (variant === undefined) {
      return this.renderText(typographyProps);
    }
    return (
      <Typography variant={variant} {...typographyProps}>
        {text}
      </Typography>
    );
  }
}

/** @type {React.ComponentClass<I18nComponentProps>} */
const I18n = compose(withServices)(I18nComponent);

export default I18n;
